import { AxiosResponse, AxiosError } from "axios";
import api from "@services/axios/apiManager";

/** PATCH /app-api/customer/contexts/:context_id 回傳的更新後 context（精簡） */
export interface RenamedContext {
  context_id: number;
  context_name: string;
}

type RenameContextResult =
  | { state: "success"; data: RenamedContext }
  | { state: "error"; error: string };

/**
 * 更新 context 名稱（customer_context.context_name）。
 * 權限：只有 owner 能改、context 必須 active（後端驗，失敗回對應錯誤訊息）。
 */
export const handleAPIRenameContext = async (
  contextId: string | number,
  contextName: string,
  cusJwtToken: string,
): Promise<RenameContextResult> => {
  try {
    const res: AxiosResponse = await api.patch(
      `/app-api/customer/contexts/${contextId}`,
      { context_name: contextName },
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success", data: res.data?.data as RenamedContext };
  } catch (error) {
    const axiosError = error as AxiosError;
    const message =
      (axiosError.response?.data as { message?: string } | undefined)?.message ||
      axiosError.message ||
      "更新失敗";
    return { state: "error", error: message };
  }
};

/** DELETE /app-api/customer/contexts/:context_id/members/:customer_id 回傳 */
export interface KickedMember {
  customer_id: number;
}

type KickMemberResult =
  | { state: "success"; data: KickedMember }
  | { state: "error"; error: string };

/**
 * 將成員移出 context（customer_context_member）。
 * 權限：只有 owner 能移除；不可移除負責人本身 / 最後一位成員（後端驗，失敗回對應錯誤訊息）。
 */
export const handleAPIKickMember = async (
  contextId: string | number,
  targetCustomerId: number,
  cusJwtToken: string,
): Promise<KickMemberResult> => {
  try {
    const res: AxiosResponse = await api.delete(
      `/app-api/customer/contexts/${contextId}/members/${targetCustomerId}`,
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success", data: res.data?.data as KickedMember };
  } catch (error) {
    const axiosError = error as AxiosError;
    const message =
      (axiosError.response?.data as { message?: string } | undefined)?.message ||
      axiosError.message ||
      "移除失敗";
    return { state: "error", error: message };
  }
};

/** 共用：錯誤訊息取後端 message，沒有就用預設字 */
const errorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError;
  return (axiosError.response?.data as { message?: string } | undefined)?.message || axiosError.message || fallback;
};

/** GET /app-api/customer/contexts/:context_id/members 的單筆成員 */
export interface ContextMember {
  customer_id: number;
  customer_name: string;
  role: "owner" | "member";
  status: "active" | "invited" | "removed" | string;
  /** 是否為可付款成員；申請中（invited）的人代表「申請付款權限」 */
  is_payer: boolean;
  joined_at: string | null;
}

type ListContextMembersResult =
  | { state: "success"; data: ContextMember[] }
  | { state: "error"; error: string };

/**
 * 列出 context 成員（customer_context_member）。
 * status 預設 active；invited（申請中，例如付款連結付完款申請加入的人）只有 owner 能看，非 owner 403。
 */
export const handleAPIListContextMembers = async (
  contextId: string | number,
  cusJwtToken: string,
  status: "active" | "invited" = "active",
): Promise<ListContextMembersResult> => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/customer/contexts/${contextId}/members`, {
      params: { status },
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    const data = res.data?.data;
    return { state: "success", data: Array.isArray(data) ? (data as ContextMember[]) : [] };
  } catch (error) {
    return { state: "error", error: errorMessage(error, "讀取成員失敗") };
  }
};

type MemberActionResult =
  | { state: "success"; data: KickedMember }
  | { state: "error"; error: string };

/** POST /app-api/customer/contexts/:context_id/members/:customer_id/approve — 負責人核准申請加入。 */
export const handleAPIApproveMember = async (
  contextId: string | number,
  targetCustomerId: number,
  cusJwtToken: string,
): Promise<MemberActionResult> => {
  try {
    const res: AxiosResponse = await api.post(
      `/app-api/customer/contexts/${contextId}/members/${targetCustomerId}/approve`,
      {},
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success", data: res.data?.data as KickedMember };
  } catch (error) {
    return { state: "error", error: errorMessage(error, "核准失敗") };
  }
};

/** POST /app-api/customer/contexts/:context_id/members/:customer_id/reject — 負責人拒絕申請加入。 */
export const handleAPIRejectMember = async (
  contextId: string | number,
  targetCustomerId: number,
  cusJwtToken: string,
): Promise<MemberActionResult> => {
  try {
    const res: AxiosResponse = await api.post(
      `/app-api/customer/contexts/${contextId}/members/${targetCustomerId}/reject`,
      {},
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success", data: res.data?.data as KickedMember };
  } catch (error) {
    return { state: "error", error: errorMessage(error, "拒絕失敗") };
  }
};

type GuestJoinSettingResult =
  | { state: "success"; data: { guest_auto_join: boolean } }
  | { state: "error"; error: string };

/**
 * GET /app-api/customer/contexts/:context_id/guest-join — 負責人查「陌生訪客自動加入」
 * （用付款連結付完款的人申請加入時，打開＝直接加入、關閉＝要負責人核准）。只有 owner 能呼叫，非 owner 403。
 */
export const handleAPIGetGuestJoinSetting = async (
  contextId: string | number,
  cusJwtToken: string,
): Promise<GuestJoinSettingResult> => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/customer/contexts/${contextId}/guest-join`, {
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return { state: "success", data: { guest_auto_join: res.data?.data?.guest_auto_join === true } };
  } catch (error) {
    return { state: "error", error: errorMessage(error, "讀取設定失敗") };
  }
};

/** PATCH /app-api/customer/contexts/:context_id/guest-join — 負責人開關「陌生訪客自動加入」（只有 owner）。 */
export const handleAPIUpdateGuestJoinSetting = async (
  contextId: string | number,
  guestAutoJoin: boolean,
  cusJwtToken: string,
): Promise<GuestJoinSettingResult> => {
  try {
    const res: AxiosResponse = await api.patch(
      `/app-api/customer/contexts/${contextId}/guest-join`,
      { guest_auto_join: guestAutoJoin },
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success", data: { guest_auto_join: res.data?.data?.guest_auto_join === true } };
  } catch (error) {
    return { state: "error", error: errorMessage(error, "更新設定失敗") };
  }
};
